/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: components/FeatureCodeShuttle.tsx
 🗓️ Created: 2025-01-27
 🧩 Description:
    Shuttle component for selecting Feature Codes.
────────────────────────────────────────────────────────────────*/

import { useState, useMemo } from 'react';
import { Search, ArrowLeft, ArrowRight } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { FeatureCode } from '../types';
import { filterAvailableFeatures, filterEnabledFeatures } from '../utils/featureHelpers';
import {
  getFeatureCodeByNumber,
  getFeatureCodeTranslationKey,
} from '../../../constants/featureCodes';
import { useI18n } from '../../../hooks/useI18n';

interface FeatureCodeShuttleProps {
  availableFeatures: FeatureCode[];
  selectedFeatureIds: Set<number>;
  onMoveToEnabled: (featureIds: number[]) => void;
  onMoveToAvailable: (featureIds: number[]) => void;
}

export function FeatureCodeShuttle({
  availableFeatures,
  selectedFeatureIds,
  onMoveToEnabled,
  onMoveToAvailable,
}: FeatureCodeShuttleProps) {
  const { t } = useI18n();
  const [searchAvailable, setSearchAvailable] = useState('');
  const [searchEnabled, setSearchEnabled] = useState('');

  const getFeatureDisplayName = (feature: FeatureCode): string => {
    const def = getFeatureCodeByNumber(feature.defaultNumber);
    if (!def) return feature.name;

    const key = getFeatureCodeTranslationKey(def);
    const translated = t(key as any);
    return translated || feature.name;
  };

  const availableFeaturesFiltered = useMemo(
    () => filterAvailableFeatures(availableFeatures, selectedFeatureIds, searchAvailable),
    [availableFeatures, selectedFeatureIds, searchAvailable]
  );

  const enabledFeaturesFiltered = useMemo(
    () => filterEnabledFeatures(availableFeatures, selectedFeatureIds, searchEnabled),
    [availableFeatures, selectedFeatureIds, searchEnabled]
  );

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">
          {t('featureCategories.selectFeatures')}
        </h3>
        <div className="text-sm text-muted-foreground">
          {selectedFeatureIds.size} {t('featureCategories.selectedFeatures')}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-end mb-3">
            <button
              type="button"
              onClick={() => onMoveToEnabled(availableFeaturesFiltered.map((f) => f.id))}
              className="text-xs px-2 py-1 text-primary hover:bg-primary/10 rounded transition-colors disabled:opacity-50"
              disabled={availableFeaturesFiltered.length === 0}
            >
              {t('featureCategories.actions.addAll')}
            </button>
          </div>

          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t('featureCategories.searchFeatures')}
              value={searchAvailable}
              onChange={(e) => setSearchAvailable(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="border border-border rounded-lg max-h-[500px] overflow-y-auto">
            {availableFeaturesFiltered.map((feature) => (
              <button
                key={feature.id}
                type="button"
                className="w-full flex items-center gap-3 p-3 border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors"
                onClick={() => onMoveToEnabled([feature.id])}
              >
                <div className="flex-1 min-w-0 text-left">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-primary">
                      {feature.customNumber || feature.defaultNumber}
                    </span>
                    <span className="font-medium text-sm text-foreground truncate">
                      {getFeatureDisplayName(feature)}
                    </span>
                  </div>
                  {feature.description && (
                    <div className="text-xs text-muted-foreground truncate">
                      {feature.description}
                    </div>
                  )}
                </div>
                <div className="w-4 h-4 rounded border-2 border-border flex items-center justify-center flex-shrink-0">
                  <ArrowRight className="h-3 w-3 text-muted-foreground" aria-hidden />
                </div>
              </button>
            ))}

            {availableFeaturesFiltered.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                {searchAvailable ? t('common.noResults') : t('featureCategories.noAvailableFeatures')}
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-end mb-3">
            <button
              type="button"
              onClick={() => onMoveToAvailable(enabledFeaturesFiltered.map((f) => f.id))}
              className="text-xs px-2 py-1 text-muted-foreground hover:bg-muted/50 rounded transition-colors disabled:opacity-50"
              disabled={enabledFeaturesFiltered.length === 0}
            >
              {t('featureCategories.actions.removeAll')}
            </button>
          </div>

          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t('featureCategories.searchFeatures')}
              value={searchEnabled}
              onChange={(e) => setSearchEnabled(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="border border-border rounded-lg max-h-[500px] overflow-y-auto">
            {enabledFeaturesFiltered.map((feature) => (
              <button
                key={feature.id}
                type="button"
                className="w-full flex items-center gap-3 p-3 border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors"
                onClick={() => onMoveToAvailable([feature.id])}
              >
                <div className="w-4 h-4 rounded border-2 border-border flex items-center justify-center flex-shrink-0">
                  <ArrowLeft className="h-3 w-3 text-muted-foreground" aria-hidden />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-primary">
                      {feature.customNumber || feature.defaultNumber}
                    </span>
                    <span className="font-medium text-sm text-foreground truncate">
                      {getFeatureDisplayName(feature)}
                    </span>
                  </div>
                  {feature.description && (
                    <div className="text-xs text-muted-foreground truncate">
                      {feature.description}
                    </div>
                  )}
                </div>
              </button>
            ))}

            {enabledFeaturesFiltered.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                {searchEnabled ? t('common.noResults') : t('featureCategories.noEnabledFeatures')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

